// @ts-ignore
import React from 'react';
// @ts-ignore
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { api } from '../../../utils/apiUtils';

// API URL configuration
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const AdminLoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  background: linear-gradient(135deg, #f0f4ff 0%, #e6f0ff 100%);
  position: relative;
  font-family: 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  padding: 20px;

  @media (max-width: 480px) {
    padding: 15px;
    justify-content: flex-start;
    padding-top: 80px;
  }
`;

const LoginCard = styled.div`
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(76, 110, 245, 0.1);
  padding: 2rem 2.5rem;
  width: 100%;
  max-width: 480px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: transform 0.3s ease;
  animation: ${fadeIn} 0.5s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(76, 110, 245, 0.15);
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 12px;

    &:hover {
      transform: none;
    }
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1.8rem;
`;

const LogoImage = styled.img`
  max-width: 339px;
  max-height: 75px;
  margin-bottom: 10px;
  object-fit: contain;

  @media (max-width: 480px) {
    max-width: 260px;
    max-height: 55px;
  }
`;

const LogoText = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  text-align: center;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.5px;

  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 4px;
    background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
    border-radius: 2px;
    margin: 10px auto 0;
  }
`;

const Description = styled.p`
  text-align: center;
  color: #718096;
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const Button = styled.button`
  display: block;
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(76, 110, 245, 0.2);
  min-height: 48px;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(76, 110, 245, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(76, 110, 245, 0.2);
  }

  &:disabled {
    background: #e2e8f0;
    color: #a0aec0;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }

  @media (max-width: 480px) {
    padding: 16px 20px;
  }
`;

const HomeButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 12px 18px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(76, 110, 245, 0.2);
  min-height: 44px;
  -webkit-tap-highlight-color: transparent;

  @media (max-width: 480px) {
    padding: 10px 14px;
    font-size: 0.85rem;
    top: 15px;
    left: 15px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(76, 110, 245, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(76, 110, 245, 0.2);
  }

  span {
    font-size: 18px;
    line-height: 1;
  }
`;

const Message = styled.div<{ type: 'error' | 'success' | 'info' }>`
  font-size: 0.95rem;
  margin-top: 1rem;
  padding: 12px 16px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  animation: ${fadeIn} 0.3s ease;

  ${props => props.type === 'error' && `
    color: #c53030;
    background-color: #fff5f5;
    border: 1px solid #feb2b2;
  `}

  ${props => props.type === 'success' && `
    color: #276749;
    background-color: #f0fff4;
    border: 1px solid #9ae6b4;
  `}

  ${props => props.type === 'info' && `
    color: #2b6cb0;
    background-color: #ebf8ff;
    border: 1px solid #90cdf4;
  `}
`;

const OTPContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 1rem 0;
`;

const OTPHorizontalLayout = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const OTPLeftSection = styled.div`
  flex-shrink: 0;
`;

const OTPRightSection = styled.div`
  flex: 1;
  text-align: center;
`;

const OTPInput = styled.input`
  width: 50px;
  height: 60px;
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  color: #2d3748;
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e0;
  }

  &:focus {
    outline: none;
    border-color: #4c6ef5;
    box-shadow: 0 0 0 4px rgba(76, 110, 245, 0.15);
    background: white;
  }

  &::placeholder {
    color: #cbd5e0;
  }
`;

const ResendLink = styled.button`
  background: none;
  border: none;
  color: #4c6ef5;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 1rem;

  &:hover {
    color: #3b5bdb;
  }

  &:disabled {
    color: #a0aec0;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const Spinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid white;
  border-radius: 50%;
  border-top-color: transparent;
  animation: ${spin} 0.8s linear infinite;
`;

const EmailIcon = styled.div<{ $horizontal?: boolean }>`
  width: ${props => props.$horizontal ? '70px' : '80px'};
  height: ${props => props.$horizontal ? '70px' : '80px'};
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${props => props.$horizontal ? '0' : '0 auto 1.5rem'};
  animation: ${pulse} 2s ease infinite;
  flex-shrink: 0;

  svg {
    width: ${props => props.$horizontal ? '35px' : '40px'};
    height: ${props => props.$horizontal ? '35px' : '40px'};
    color: white;
  }
`;

// ─── Styled components for Gate inputs ──────────────────────────────────
const GateInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 16px;
  background: #f8fafc;
  color: #2d3748;
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e0;
  }

  &:focus {
    outline: none;
    border-color: #4c6ef5;
    box-shadow: 0 0 0 4px rgba(76, 110, 245, 0.15);
    background: white;
  }

  &::placeholder {
    color: #cbd5e0;
  }
`;

const GateLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2d3748;
  font-size: 0.95rem;
`;

const GateFieldGroup = styled.div`
  margin-bottom: 1rem;
`;

const ScareMessage = styled.p`
  text-align: center;
  color: #c53030;
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 1.2rem;
  line-height: 1.5;
  padding: 10px 14px;
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 8px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
`;

// ─── Component ──────────────────────────────────────────────────────────
type LoginStep = 'loading' | 'gate_questions' | 'gate_dummy_otp' | 'initial' | 'otp';

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<LoginStep>('loading');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'error' | 'success' | 'info'; text: string } | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Gate state
  const [gateEnabled, setGateEnabled] = useState<boolean | null>(null);
  const [gateQuestions, setGateQuestions] = useState<{ label: string }[]>([]);
  const [scareMessage, setScareMessage] = useState('');
  const [gateAnswers, setGateAnswers] = useState<Record<string, string>>({});
  const [dummyOtp, setDummyOtp] = useState(['', '', '', '', '', '']);
  const dummyOtpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [isLockedOut, setIsLockedOut] = useState(false);

  // Fetch gate config on mount
  useEffect(() => {
    const fetchGateConfig = async () => {
      try {
        const response = await api.get('/admin/gate/config');
        if (response.data.success && response.data.gateEnabled) {
          setGateEnabled(true);
          setGateQuestions(response.data.questions || []);
          setScareMessage(response.data.scareMessage || '');
          setStep('gate_questions');
        } else {
          setGateEnabled(false);
          setStep('initial');
        }
      } catch {
        setGateEnabled(false);
        setStep('initial');
      }
    };
    fetchGateConfig();
  }, []);

  // Cooldown timer for real OTP
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (cooldown === 0 && message?.type === 'info' && message?.text?.includes('seconds before requesting')) {
      setMessage(null);
    }
  }, [cooldown, message]);

  useEffect(() => {
    if (cooldown > 0 && message?.type === 'info' && message?.text?.includes('seconds before requesting')) {
      setMessage({ type: 'info', text: `Please wait ${cooldown} seconds before requesting another OTP.` });
    }
  }, [cooldown]);

  // Auto-verify real OTP when all 6 digits entered
  useEffect(() => {
    const otpValue = otp.join('');
    if (otpValue.length === 6 && step === 'otp') {
      handleVerifyOTP(otpValue);
    }
  }, [otp, step]);

  // Auto-verify dummy OTP when all 6 digits entered
  useEffect(() => {
    const otpValue = dummyOtp.join('');
    if (otpValue.length === 6 && step === 'gate_dummy_otp') {
      handleDummyOtpVerify(otpValue);
    }
  }, [dummyOtp, step]);

  // ─── Gate Questions Handler ─────────────────────────────────────────
  const handleGateQuestions = async () => {
    setIsLoading(true);
    setMessage(null);

    const answers = gateQuestions.map(q => ({
      label: q.label,
      answer: gateAnswers[q.label] || ''
    }));

    try {
      const response = await api.post('/admin/gate/verify', { answers });
      if (response.data.success) {
        setStep('gate_dummy_otp');
        setMessage(null);
        setTimeout(() => dummyOtpRefs.current[0]?.focus(), 100);
      }
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Verification failed.';
      setMessage({ type: 'error', text: msg });
      if (error.response?.status === 429) {
        setIsLockedOut(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Dummy OTP Handler ──────────────────────────────────────────────
  const handleDummyOtpVerify = async (otpValue: string) => {
    setIsLoading(true);
    setMessage(null);

    try {
      const response = await api.post('/admin/gate/verify-otp', { otp: otpValue });
      if (response.data.success) {
        setStep('initial');
        setMessage({ type: 'success', text: 'Verification complete.' });
        setTimeout(() => setMessage(null), 2000);
      }
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Invalid OTP.';
      setMessage({ type: 'error', text: msg });
      setDummyOtp(['', '', '', '', '', '']);
      if (error.response?.status === 429) {
        setIsLockedOut(true);
      } else {
        setTimeout(() => dummyOtpRefs.current[0]?.focus(), 100);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Dummy OTP Input Handlers ───────────────────────────────────────
  const handleDummyOtpChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...dummyOtp];
    newOtp[index] = value;
    setDummyOtp(newOtp);
    if (value && index < 5) {
      dummyOtpRefs.current[index + 1]?.focus();
    }
  };

  const handleDummyOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !dummyOtp[index] && index > 0) {
      dummyOtpRefs.current[index - 1]?.focus();
    }
  };

  const handleDummyOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setDummyOtp(digits);
      dummyOtpRefs.current[5]?.focus();
    }
  };

  // ─── Existing Handlers (unchanged) ─────────────────────────────────
  const handleSendOTP = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      const response = await api.post('/admin/send-otp', {});

      if (response.data.success) {
        setStep('otp');
        setMessage({ type: 'success', text: 'OTP sent to admin email addresses. Please check your inbox.' });
        setCooldown(60);
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to send OTP.' });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to send OTP. Please try again.';
      const rateLimitMatch = errorMessage.match(/Please wait (\d+) seconds/);
      if (rateLimitMatch) {
        const remainingSeconds = parseInt(rateLimitMatch[1], 10);
        setCooldown(remainingSeconds);
        setMessage({ type: 'info', text: `Please wait ${remainingSeconds} seconds before requesting another OTP.` });
      } else {
        setMessage({ type: 'error', text: errorMessage });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (otpValue: string) => {
    setIsLoading(true);
    setMessage(null);

    try {
      const response = await api.post('/admin/verify-otp', { otp: otpValue });

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Login successful! Redirecting...' });
        if (response.data.admin_token) {
          localStorage.setItem('adminToken', response.data.admin_token);
        }
        localStorage.setItem('adminAuth', 'true');
        localStorage.setItem('adminUser', JSON.stringify({ username: 'admin', role: 'admin' }));
        setTimeout(() => navigate('/admin/dashboard'), 500);
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Invalid OTP.' });
        setOtp(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'OTP verification failed.' });
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtp(digits);
      otpRefs.current[5]?.focus();
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────
  return (
    <AdminLoginContainer>
      <HomeButton onClick={() => navigate('/')}>
        <span>&#8592;</span> Home
      </HomeButton>
      <LoginCard>
        <LogoContainer>
          <LogoImage src="/assets/logo.png" alt="CNG Dealership Logo" />
          <LogoText>Admin Portal</LogoText>
        </LogoContainer>

        {/* Loading state while fetching gate config */}
        {step === 'loading' && (
          <LoadingContainer>
            <Spinner style={{ borderColor: '#4c6ef5', borderTopColor: 'transparent', width: '30px', height: '30px' }} />
          </LoadingContainer>
        )}

        {/* Step 1: Gate Security Questions */}
        {step === 'gate_questions' && (
          <>
            <Description>
              Please answer the security questions below to proceed.
            </Description>
            {gateQuestions.map((q, index) => (
              <GateFieldGroup key={q.label}>
                <GateLabel>{q.label}</GateLabel>
                <GateInput
                  type="text"
                  value={gateAnswers[q.label] || ''}
                  onChange={(e) => setGateAnswers(prev => ({ ...prev, [q.label]: e.target.value }))}
                  autoFocus={index === 0}
                  disabled={isLoading || isLockedOut}
                  placeholder="Enter your answer"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleGateQuestions();
                  }}
                />
              </GateFieldGroup>
            ))}
            <Button onClick={handleGateQuestions} disabled={isLoading || isLockedOut}>
              {isLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <Spinner /> Verifying...
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  Continue
                </span>
              )}
            </Button>
          </>
        )}

        {/* Step 2: Gate Dummy OTP with Scare Message */}
        {step === 'gate_dummy_otp' && (
          <>
            {scareMessage && (
              <ScareMessage>{scareMessage}</ScareMessage>
            )}
            <Description style={{ fontSize: '0.9rem' }}>
              Enter the 6-digit verification code to proceed.
            </Description>
            <OTPContainer onPaste={handleDummyOtpPaste}>
              {dummyOtp.map((digit, index) => (
                <OTPInput
                  key={`dummy-${index}`}
                  ref={el => { dummyOtpRefs.current[index] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleDummyOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleDummyOtpKeyDown(index, e)}
                  disabled={isLoading || isLockedOut}
                  placeholder="•"
                  autoFocus={index === 0}
                  style={{ width: '42px', height: '50px', fontSize: '20px' }}
                />
              ))}
            </OTPContainer>
            {isLoading && (
              <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
                <Spinner style={{ borderColor: '#4c6ef5', borderTopColor: 'transparent' }} />
                <p style={{ color: '#718096', marginTop: '0.5rem', fontSize: '0.85rem' }}>Verifying...</p>
              </div>
            )}
          </>
        )}

        {/* Step 3: Send Real OTP (existing, unchanged) */}
        {step === 'initial' && (
          <>
            <Description>
              Click the button below to receive a One-Time Password (OTP) on the registered admin email addresses.
            </Description>
            <Button onClick={handleSendOTP} disabled={isLoading}>
              {isLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <Spinner /> Sending OTP...
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  Send OTP to Login
                </span>
              )}
            </Button>
          </>
        )}

        {/* Step 4: Enter Real OTP (existing, unchanged) */}
        {step === 'otp' && (
          <OTPHorizontalLayout>
            <OTPLeftSection>
              <EmailIcon $horizontal>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </EmailIcon>
            </OTPLeftSection>
            <OTPRightSection>
              <Description style={{ marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                Enter the 6-digit OTP sent to admin email addresses
              </Description>

              <OTPContainer onPaste={handleOtpPaste}>
                {otp.map((digit, index) => (
                  <OTPInput
                    key={index}
                    ref={el => otpRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    disabled={isLoading}
                    placeholder="•"
                    autoFocus={index === 0}
                    style={{ width: '42px', height: '50px', fontSize: '20px' }}
                  />
                ))}
              </OTPContainer>

              {isLoading && (
                <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
                  <Spinner style={{ borderColor: '#4c6ef5', borderTopColor: 'transparent' }} />
                  <p style={{ color: '#718096', marginTop: '0.5rem', fontSize: '0.85rem' }}>Verifying...</p>
                </div>
              )}

              <ResendLink
                onClick={handleSendOTP}
                disabled={cooldown > 0 || isLoading}
                style={{ marginTop: '0.5rem' }}
              >
                {cooldown > 0 ? `Resend OTP in ${cooldown}s` : 'Resend OTP'}
              </ResendLink>
            </OTPRightSection>
          </OTPHorizontalLayout>
        )}

        {message && (
          <Message type={message.type}>
            {message.type === 'error' && '\u26A0\uFE0F'}
            {message.type === 'success' && '\u2713'}
            {message.type === 'info' && '\u2139\uFE0F'}
            {message.text}
          </Message>
        )}
      </LoginCard>
    </AdminLoginContainer>
  );
};

export default AdminLogin;
